import { useState } from 'react';
import { toast } from 'sonner';
import { History, Laptop, Pencil, Plus, Undo2, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { useAssetHistoryQuery, useAssetsQuery, useAssignAssetMutation, useUpsertAssetMutation, useVendorsQuery } from '@/client/api/business/spend';
import { ASSET_STATUS_BADGE, type AssetCategory, type BizAsset } from '@/client/types/business/spend';
import { BusinessPageShell } from '../components/BusinessPageShell';
import { Field, filterClass, inputClass, textareaClass } from '../../Accounting/components/spend-ui';
import { DeleteConfirmDialog, EmptyState, EmployeeLink, RowMenu, StatusBadge } from '../../HumanResource/components/hr-ui';
import { money } from '../../HumanResource/components/comp-format';
import { formatDay } from '../../HumanResource/components/leave-format';
import { useEmployees } from '../../HumanResource/hr-store';
import { hasLeft } from '@/client/types/hr';

const CATEGORY_LABEL: Record<AssetCategory, string> = {
  laptop: 'Laptop', phone: 'Phone', tablet: 'Tablet', monitor: 'Monitor', accessory: 'Accessory', furniture: 'Furniture', vehicle: 'Vehicle', other: 'Other',
};

/** Company equipment: who has what, and its history. */
export default function AssetsPage() {
  const q = useAssetsQuery();
  const upsert = useUpsertAssetMutation();
  const assign = useAssignAssetMutation();
  const vendors = useVendorsQuery().data ?? [];
  const employees = useEmployees();
  const canManage = useGate(PERMISSIONS.accounting.manage).allowed;
  const [status, setStatus] = useState<BizAsset['status'] | 'all'>('all');
  const [form, setForm] = useState<Partial<BizAsset> | null>(null);
  const [giving, setGiving] = useState<BizAsset | null>(null);
  const [to, setTo] = useState('');
  const [taking, setTaking] = useState<BizAsset | null>(null);
  const [historyFor, setHistoryFor] = useState<BizAsset | null>(null);
  const historyQ = useAssetHistoryQuery(historyFor?.id);

  const assets = q.data ?? [];
  const shown = assets.filter((a) => status === 'all' || a.status === status);
  const person = (id?: string | null) => employees.find((e) => e.id === id);
  const soon = new Date(Date.now() + 60 * 86_400_000).toISOString().slice(0, 10);

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    const { assigneeId: _a, ...rest } = form;
    upsert.mutate(
      {
        ...rest,
        status: form.status === 'assigned' ? undefined : form.status,
        vendorId: form.vendorId || null,
        purchasedOn: form.purchasedOn || null,
        warrantyUntil: form.warrantyUntil || null,
        cost: form.cost ?? null,
        currency: form.cost ? form.currency || 'GHS' : undefined,
      },
      {
        onSuccess: () => {
          toast.success('Saved');
          setForm(null);
        },
        onError: (err) => toast.error((err as Error).message || 'Could not save'),
      },
    );
  };

  return (
    <BusinessPageShell
      mock={false}
      title="Equipment"
      description="Laptops, phones and everything else the company owns, who has each one, and since when. People see their own in My workplace."
      crumbs={[{ label: 'Assets' }, { label: 'Equipment' }]}
      icon={Laptop}
      actions={
        <PermissionButton code={PERMISSIONS.accounting.manage} className="gap-2" onClick={() => setForm({ name: '', category: 'laptop', currency: 'GHS' })}>
          <Plus className="h-4 w-4" /> Add item
        </PermissionButton>
      }
      stats={[
        { label: 'Items', value: String(assets.filter((a) => a.status !== 'retired').length) },
        { label: 'With someone', value: String(assets.filter((a) => a.status === 'assigned').length) },
        { label: 'In stock', value: String(assets.filter((a) => a.status === 'in_stock').length) },
        { label: 'Warranty ending in 60 days', value: String(assets.filter((a) => a.warrantyUntil && a.warrantyUntil <= soon && a.status !== 'retired').length) },
      ]}
    >
      <div className="mb-4">
        <select value={status} onChange={(e) => setStatus(e.target.value as BizAsset['status'] | 'all')} className={filterClass}>
          <option value="all">Any status</option>
          {Object.entries(ASSET_STATUS_BADGE).map(([v, s]) => (
            <option key={v} value={v}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
      {q.isError ? (
        <EmptyState label={`Could not load equipment: ${(q.error as Error)?.message ?? 'unknown error'}`} />
      ) : q.isLoading ? (
        <EmptyState label="Loading equipment…" />
      ) : shown.length === 0 ? (
        <EmptyState label="No equipment recorded yet." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Item</th>
                <th className="px-4 py-3 font-medium">With</th>
                <th className="px-4 py-3 font-medium">Bought</th>
                <th className="px-4 py-3 font-medium">Warranty</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {shown.map((a) => (
                <tr key={a.id} className={canManage ? 'cursor-pointer hover:bg-gray-50' : ''} onClick={() => canManage && setForm(a)}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{a.name}</p>
                    <p className="text-xs text-gray-400">
                      {a.tag} · {CATEGORY_LABEL[a.category]}
                      {a.serial && ` · ${a.serial}`}
                    </p>
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    {a.assigneeId ? <EmployeeLink employee={person(a.assigneeId)} /> : <span className="text-gray-400">{a.location || '—'}</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {a.purchasedOn ? formatDay(a.purchasedOn) : '—'}
                    {a.cost != null && a.currency && <p className="text-xs text-gray-400">{money(a.cost, a.currency)}</p>}
                  </td>
                  <td className={`px-4 py-3 ${a.warrantyUntil && a.warrantyUntil <= soon ? 'text-amber-700' : 'text-gray-600'}`}>
                    {a.warrantyUntil ? formatDay(a.warrantyUntil) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge label={ASSET_STATUS_BADGE[a.status].label} className={ASSET_STATUS_BADGE[a.status].className} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <RowMenu
                      items={[
                        { label: a.assigneeId ? 'Give to someone else' : 'Give to someone', icon: UserPlus, hidden: !canManage || a.status === 'retired' || a.status === 'lost', onSelect: () => setGiving(a) },
                        { label: 'Take back', icon: Undo2, hidden: !canManage || !a.assigneeId, onSelect: () => setTaking(a) },
                        { label: 'Edit', icon: Pencil, hidden: !canManage, onSelect: () => setForm(a) },
                        { label: 'History', icon: History, onSelect: () => setHistoryFor(a) },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{form?.id ? `Edit ${form.tag}` : 'Add equipment'}</DialogTitle>
          </DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Name">
                  <input required value={form.name ?? ''} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} placeholder="MacBook Air 13" />
                </Field>
                <Field label="Kind">
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as AssetCategory })} className={inputClass}>
                    {Object.entries(CATEGORY_LABEL).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Tag (empty = next number)">
                  <input value={form.tag ?? ''} onChange={(e) => setForm({ ...form, tag: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Serial number">
                  <input value={form.serial ?? ''} onChange={(e) => setForm({ ...form, serial: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Bought on">
                  <input type="date" value={form.purchasedOn ?? ''} onChange={(e) => setForm({ ...form, purchasedOn: e.target.value })} className={inputClass} />
                </Field>
                <Field label="From">
                  <select value={form.vendorId ?? ''} onChange={(e) => setForm({ ...form, vendorId: e.target.value })} className={inputClass}>
                    <option value="">—</option>
                    {vendors.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Cost">
                  <input type="number" min={0} step="0.01" value={form.cost ?? ''} onChange={(e) => setForm({ ...form, cost: e.target.value === '' ? null : Number(e.target.value) })} className={inputClass} />
                </Field>
                <Field label="Currency">
                  <input maxLength={3} value={form.currency ?? 'GHS'} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} className={inputClass} />
                </Field>
                <Field label="Warranty until">
                  <input type="date" value={form.warrantyUntil ?? ''} onChange={(e) => setForm({ ...form, warrantyUntil: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Where (when not with someone)">
                  <input value={form.location ?? ''} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputClass} placeholder="Office cupboard" />
                </Field>
                {form.id && form.status !== 'assigned' && (
                  <Field label="Status">
                    <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as BizAsset['status'] })} className={inputClass}>
                      {(['in_stock', 'repair', 'retired', 'lost'] as const).map((v) => (
                        <option key={v} value={v}>
                          {ASSET_STATUS_BADGE[v].label}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
              </div>
              <Field label="Notes">
                <textarea rows={2} value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={textareaClass} />
              </Field>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={() => setForm(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={upsert.isPending}>
                  Save
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!giving} onOpenChange={(o) => !o && setGiving(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Give {giving?.name} to…</DialogTitle>
          </DialogHeader>
          <select value={to} onChange={(e) => setTo(e.target.value)} className={inputClass}>
            <option value="">Pick someone</option>
            {employees
              .filter((e) => !hasLeft(e) && e.id !== giving?.assigneeId)
              .map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
          </select>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setGiving(null)}>
              Cancel
            </Button>
            <Button
              disabled={!to}
              onClick={() => {
                if (giving) assign.mutate({ id: giving.id, employeeId: to }, { onSuccess: () => toast.success('Assigned'), onError: (e) => toast.error((e as Error).message) });
                setGiving(null);
                setTo('');
              }}
            >
              Give
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!taking}
        onOpenChange={(o) => !o && setTaking(null)}
        title={`Take back ${taking?.name}?`}
        description={`${person(taking?.assigneeId)?.name ?? 'They'} no longer has it; it goes back in stock. The history keeps who had it.`}
        actionLabel="Take back"
        danger={false}
        onConfirm={() => {
          if (taking) assign.mutate({ id: taking.id, employeeId: null }, { onSuccess: () => toast.success('Back in stock'), onError: (e) => toast.error((e as Error).message) });
          setTaking(null);
        }}
      />

      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{historyFor?.tag} history</DialogTitle>
          </DialogHeader>
          {historyQ.isLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (historyQ.data ?? []).length === 0 ? (
            <p className="text-sm text-gray-500">Never given to anyone.</p>
          ) : (
            <ul className="divide-y divide-gray-50">
              {(historyQ.data ?? []).map((h, i) => (
                <li key={i} className="py-2 text-sm">
                  <span className="text-gray-900">{person(h.employeeId)?.name ?? 'Former employee'}</span>
                  <span className="ml-2 text-xs text-gray-500">
                    {formatDay(h.assignedOn)} – {h.returnedOn ? formatDay(h.returnedOn) : 'now'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </BusinessPageShell>
  );
}
